import { createContext, useContext, useState } from 'react';

/**
 * Where a floating panel should portal (operator-parity phase 21).
 *
 * The companion to `layers.js`. That module answers "which layer am I?"; this
 * one answers "am I inside another layer?" — and the second question is not a
 * number. `CgDialog`'s content node is `position: fixed` with a permanent
 * `-translate-x-1/2 -translate-y-1/2`, and a transform creates a stacking
 * context: a descendant at 999999 still loses to a body-level sibling at
 * 10070. Portaling into the host is the only thing that orders it correctly.
 *
 * ── Why one context and not two ─────────────────────────────────────────────
 *
 * `CgDialog` and `CgDrawer` each already publish their own content node, and
 * consumers combined them as `dialog ?? drawer`. That is wrong for one of the
 * two nesting orders: a popover inside a drawer that is itself inside a dialog
 * resolves to the *dialog* — the lower rung — and paints behind the drawer it
 * lives in. Flipping the `??` just moves the bug to the other order.
 *
 * A single context has no ordering to get wrong. React resolves to the nearest
 * provider above, so the innermost host always wins, whichever way they nest.
 *
 * The per-component `useCgDialogPortalContainer` / `useCgDrawerPortalContainer`
 * hooks stay for the react-select call sites that deliberately target dialogs
 * only (`CgSelect`, `SearchSelectField`, `StockSalesOrderModal`) — switching
 * those to the host would change where they portal inside drawers, which is a
 * separate, verifiable change rather than plumbing.
 */
const OverlayHostContext = createContext(null);

export const OverlayHostProvider = OverlayHostContext.Provider;

/** The innermost CgDialog/CgDrawer content node, or null on a plain page. */
export const useOverlayHost = () => useContext(OverlayHostContext);

/**
 * Portal container for a panel whose component renders no DOM of its own
 * (Radix `asChild` triggers: CgTooltip, BlockContextMenu, CgFormLabel).
 *
 * `undefined` rather than null on purpose — `@radix-ui/react-portal` does
 * `containerProp || document.body`, so undefined restores Radix's default and
 * the call site is unchanged on a plain page.
 */
export const useOverlayPortalContainer = () => useOverlayHost() ?? undefined;

/**
 * As above, plus the legacy react-bootstrap `.modal` fallback.
 *
 * That fallback needs a real DOM node to walk up from, so this returns a ref
 * callback for the component's own wrapper element — only usable by components
 * that already render a wrapper (`CgFilterDropdown`, `CgDatePicker`). Adding
 * one purely to get the fallback would inject a block-level element into the
 * call site's layout, which is why the hooks are split.
 *
 * Why `.modal` matters: it enforces focus by yanking any `focusin` outside
 * itself back to the dialog, so a panel portaled to `<body>` lost focus the
 * moment it opened, Radix read that as focus-outside and dismissed the layer —
 * the dropdown flickered open/shut and no option could be clicked. Nothing
 * clips the panel there: Radix positions with `strategy: 'fixed'` and `.modal`
 * sets no transform (Bootstrap animates `.modal-dialog` instead), so it is not
 * a containing block for fixed descendants.
 *
 * @returns {[(node: HTMLElement | null) => void, HTMLElement | undefined]}
 */
export const usePopoverPortalContainer = () => {
  const host = useOverlayHost();
  const [wrapper, setWrapper] = useState(null);
  // Radix hosts win over the bootstrap fallback: inside a CgDrawer the drawer's
  // node is the correct target even if that drawer is itself nested in a
  // legacy `.modal`.
  const container = host ?? wrapper?.closest('.modal') ?? undefined;
  return [setWrapper, container];
};

/**
 * Corrective offsets for a react-select menu portaled into an overlay host.
 *
 * react-select positions a portaled menu from the control's VIEWPORT rect and
 * never accounts for where it portals to (`menuPortalCSS` is
 * `{ left: rect.left, top: offset }`, and `MenuPortal` computes `offset` as
 * `rect[placement] + window.pageYOffset`). That is right for `<body>` and wrong
 * for a CgDialog/CgDrawer content node: those are positioned — and the dialog
 * additionally carries `-translate-x-1/2 -translate-y-1/2` — so they are the
 * containing block for the portaled wrapper, and viewport coordinates land
 * offset by the host's own top-left corner. Measured before this correction: a
 * dialog at (320, 132) pushed its menus 320px right and 132px down, well clear
 * of the control they belong to.
 *
 * Subtracting the host's origin puts the menu back under its control. `<body>`
 * is passed through untouched — it is not a containing block, and its rect goes
 * negative once the page scrolls, so "correcting" against it would introduce
 * the very bug this removes.
 *
 * Usage, inside a react-select `styles` object:
 *
 *   menuPortal: (base) => ({ ...withPortalOffset(base, target), zIndex: 20001 })
 */
export const withPortalOffset = (base, target) => {
  if (!target || (typeof document !== 'undefined' && target === document.body)) return base;
  const rect = target.getBoundingClientRect();
  return { ...base, top: base.top - rect.top, left: base.left - rect.left };
};
